'use client';
import { useState } from 'react';
import { CheckIcon, CopyIcon } from 'lucide-react';
import { CodeBlock } from '~/components/playground/code-block';
import { IconButton } from '~/components/ui/IconButton';
import { ScrollArea, ScrollBar } from '~/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { useCopyToClipboard } from '~/hooks/use-copy-to-clipboard';
import { ApiCodeSamples } from './';

export interface ApiExampleProps {
  name: string;
  samples: ApiCodeSamples;
}

export const ApiExample = ({ name, samples }: ApiExampleProps) => {
  const [tab, setTab] = useState<keyof typeof samples>('typescript');

  const { isCopied, copyToClipboard } = useCopyToClipboard({ timeout: 2000 });
  const onCopy = () => {
    if (isCopied) return;
    copyToClipboard(samples[tab] as string);
  };

  return (
    <div className="w-full flex-1 overflow-hidden">
      <Tabs
        defaultValue="typescript"
        onValueChange={(value) => {
          setTab(value as any);
        }}
        className="mt-6 flex h-auto flex-col  overflow-hidden rounded-lg bg-gray-100 p-2 max-sm:max-w-[calc(100dvw-46px)] dark:bg-zinc-800"
      >
        <TabsList className="justify-between bg-transparent">
          <ScrollArea>
            <div className="flex">
              {Object.keys(samples).map((language) => (
                <TabsTrigger
                  key={`${name}-trigger-${language}`}
                  value={language}
                  className="data-[state=active]:bg-primary-200 data-[state=active]:text-white"
                >
                  {language.charAt(0).toUpperCase() + language.slice(1)}
                </TabsTrigger>
              ))}
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>

          <div className="flex justify-end space-x-1 text-zinc-700 ">
            <IconButton variant="ghost" onClick={onCopy}>
              {isCopied ? <CheckIcon className="h-4 w-4" /> : <CopyIcon className="h-4 w-4" />}
              <span className="sr-only">Copy code</span>
            </IconButton>
          </div>
        </TabsList>
        {Object.entries(samples).map(([language, sample]) => (
          <TabsContent key={`${name}-sample-${language}`} value={language}>
            <ScrollArea className="rounded-lg">
              <div className="flex">
                <CodeBlock
                  language={language}
                  header={false}
                  showLineNumbers={false}
                  copyValue={sample}
                  value={sample}
                />
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};
